import { useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import {
  createPressCode,
  getPressIngestState,
  listPressCodes,
  registerPressAudio,
  removePressAudio,
  resolvePressUploadTarget,
  setPressCodeActive,
  type PressCodeRow,
  type PressIngestState,
} from "@/lib/press-admin.functions";
import { PRESS_TRACKS } from "@/lib/press-listening";
import { PressBrandAssets } from "@/components/admin/PressBrandAssets";

export const Route = createFileRoute("/_authenticated/admin/press-room")({
  head: () => ({
    meta: [
      { title: "Press Listening Room — Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminPressRoom,
});

/** Reads duration from the file locally so we can store it alongside the asset. */
function readDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    const done = (value: number | null) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };
    audio.onloadedmetadata = () => done(Number.isFinite(audio.duration) ? Math.round(audio.duration) : null);
    audio.onerror = () => done(null);
    audio.src = url;
  });
}

const PRESS_LISTENING_URL = "https://buddhaka.com/press/the-diamond-sutra/listen";

function AdminPressRoom() {
  const [copied, setCopied] = useState(false);
  const copyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(PRESS_LISTENING_URL);
    } catch {
      const el = document.createElement("textarea");
      el.value = PRESS_LISTENING_URL;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      el.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  const loadState = useServerFn(getPressIngestState);
  const resolveTarget = useServerFn(resolvePressUploadTarget);
  const register = useServerFn(registerPressAudio);
  const remove = useServerFn(removePressAudio);
  const loadCodes = useServerFn(listPressCodes);
  const createCode = useServerFn(createPressCode);
  const setActive = useServerFn(setPressCodeActive);

  const [state, setState] = useState<PressIngestState | null>(null);
  const [codes, setCodes] = useState<PressCodeRow[]>([]);
  const [log, setLog] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    const [s, c] = await Promise.all([loadState(), loadCodes()]);
    setState(s);
    setCodes(c);
  }, [loadState, loadCodes]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function upload(files: FileList) {
    setBusy(true);
    const messages: string[] = [];
    const seen = new Set<string>();

    for (const file of Array.from(files)) {
      const target = await resolveTarget({ data: { filename: file.name } });
      if (!target.ok) {
        messages.push(`✗ ${file.name}: ${target.error}`);
        continue;
      }
      if (seen.has(target.slug)) {
        messages.push(`✗ ${file.name}: duplicate upload for “${target.title}” in this batch.`);
        continue;
      }
      seen.add(target.slug);

      if (!file.type.startsWith("audio/") && !file.name.toLowerCase().endsWith(".mp3")) {
        messages.push(`✗ ${file.name}: only MP3 audio is accepted.`);
        continue;
      }

      const duration = await readDuration(file);
      // Private bucket, private prefix — bucket publicity is never changed.
      const { error } = await supabase.storage
        .from("media")
        .upload(target.storagePath, file, { upsert: true, contentType: "audio/mpeg" });
      if (error) {
        messages.push(`✗ ${file.name}: upload failed — ${error.message}`);
        continue;
      }

      const result = await register({
        data: {
          songSlug: target.slug,
          sizeBytes: file.size,
          format: "mp3",
          ...(duration != null ? { durationSeconds: duration } : {}),
        },
      });
      messages.push(
        "error" in result ? `✗ ${file.name}: ${result.error}` : `✓ ${target.title} registered.`,
      );
    }

    setLog(messages);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
    await refresh();
  }

  const [label, setLabel] = useState("");
  const [expiry, setExpiry] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [newCode, setNewCode] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  async function issueCode(e: React.FormEvent) {
    e.preventDefault();
    setCodeError(null);
    setNewCode(null);
    const result = await createCode({
      data: {
        label,
        expiresAt: expiry ? expiry : null,
        maxUses: maxUses ? Number(maxUses) : null,
      },
    });
    if ("error" in result) {
      setCodeError(result.error);
      return;
    }
    setNewCode(result.code);
    setLabel("");
    setExpiry("");
    setMaxUses("");
    await refresh();
  }

  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <h1 className="text-2xl font-semibold tracking-tight">Diamond Sutra — Press Listening Room</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Private advance masters and press access codes. Files stay in the private media bucket; the
        room refuses playback for any track that is not registered.
      </p>

      <section className="mt-8" aria-labelledby="ingest-heading">
        <h2 id="ingest-heading" className="text-lg font-medium">
          Audio ingest
        </h2>
        {state ? (
          <p className="mt-1 text-sm" role="status">
            {state.availableCount} / {state.totalCount} masters loaded —{" "}
            <strong>{state.ready ? "READY for press" : "NOT ready"}</strong>
          </p>
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">Loading…</p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <a
            href={PRESS_LISTENING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-11 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground"
          >
            Preview Press Listening Room
          </a>
          <button
            type="button"
            onClick={copyUrl}
            className="inline-flex min-h-11 items-center rounded-md border border-border px-4 text-sm font-medium"
          >
            {copied ? "Copied" : "Copy Listening Room URL"}
          </button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground" aria-live="polite">
          Opens in a new tab exactly as a journalist sees it — the access-code gate still applies.
          {copied ? " Canonical URL copied to clipboard." : ""}
        </p>

        <label htmlFor="press-files" className="mt-4 block text-sm font-medium">
          Upload MP3 masters (multiple allowed)
        </label>
        <input
          id="press-files"
          ref={fileRef}
          type="file"
          accept="audio/mpeg,.mp3"
          multiple
          disabled={busy}
          onChange={(e) => e.target.files && e.target.files.length > 0 && void upload(e.target.files)}
          className="mt-2 min-h-11 w-full rounded-md border border-border px-3 py-2 text-sm"
        />
        <p className="mt-2 text-xs text-muted-foreground">
          Filenames must match the canonical press masters, e.g. {PRESS_TRACKS[0]!.filename}.
        </p>

        {busy ? <p className="mt-3 text-sm">Uploading…</p> : null}
        {log.length > 0 ? (
          <ul className="mt-3 space-y-1 text-sm" aria-live="polite">
            {log.map((line) => (
              <li key={line} className={line.startsWith("✗") ? "text-destructive" : ""}>
                {line}
              </li>
            ))}
          </ul>
        ) : null}

        {state && state.missing.length > 0 ? (
          <div className="mt-5 rounded-md border border-border p-3">
            <h3 className="text-sm font-medium">Missing masters</h3>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
              {state.missing.map((m) => (
                <li key={m.slug}>
                  {m.position}. {m.title} — expects {m.filename}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {state && state.assets.length > 0 ? (
          <table className="mt-5 w-full text-left text-sm">
            <caption className="sr-only">Registered press masters</caption>
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className="py-2">#</th>
                <th scope="col">Track</th>
                <th scope="col">Duration</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {state.assets.map((a) => (
                <tr key={a.songSlug} className="border-b border-border/60">
                  <td className="py-2 tabular-nums">{a.position}</td>
                  <td>{a.title}</td>
                  <td className="tabular-nums">
                    {a.durationSeconds != null
                      ? `${Math.floor(a.durationSeconds / 60)}:${String(a.durationSeconds % 60).padStart(2, "0")}`
                      : "—"}
                  </td>
                  <td className="text-right">
                    <button
                      type="button"
                      className="min-h-11 px-2 text-xs underline"
                      onClick={async () => {
                        await remove({ data: { songSlug: a.songSlug } });
                        await refresh();
                      }}
                    >
                      Unregister
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>

      <section className="mt-12" aria-labelledby="codes-heading">
        <h2 id="codes-heading" className="text-lg font-medium">
          Press access codes
        </h2>
        <form onSubmit={issueCode} className="mt-4 grid gap-3 sm:grid-cols-3">
          <div className="sm:col-span-3">
            <label htmlFor="code-label" className="block text-sm font-medium">
              Outlet / reviewer
            </label>
            <input
              id="code-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              required
              className="mt-1 min-h-11 w-full rounded-md border border-border px-3"
            />
          </div>
          <div>
            <label htmlFor="code-expiry" className="block text-sm font-medium">
              Expires (optional)
            </label>
            <input
              id="code-expiry"
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              className="mt-1 min-h-11 w-full rounded-md border border-border px-3"
            />
          </div>
          <div>
            <label htmlFor="code-uses" className="block text-sm font-medium">
              Max uses (optional)
            </label>
            <input
              id="code-uses"
              type="number"
              min={1}
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              className="mt-1 min-h-11 w-full rounded-md border border-border px-3"
            />
          </div>
          <div className="flex items-end">
            <button type="submit" className="min-h-11 w-full rounded-md bg-primary px-4 text-primary-foreground">
              Issue code
            </button>
          </div>
        </form>

        {codeError ? (
          <p role="alert" className="mt-3 text-sm text-destructive">
            {codeError}
          </p>
        ) : null}
        {newCode ? (
          <p className="mt-3 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-sm">
            New code (shown once — copy it now): <strong className="font-mono">{newCode}</strong>
          </p>
        ) : null}

        <table className="mt-6 w-full text-left text-sm">
          <caption className="sr-only">Issued press access codes</caption>
          <thead>
            <tr className="border-b border-border">
              <th scope="col" className="py-2">Outlet</th>
              <th scope="col">Uses</th>
              <th scope="col">Expires</th>
              <th scope="col">State</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {codes.map((c) => (
              <tr key={c.id} className="border-b border-border/60">
                <td className="py-2">{c.label}</td>
                <td className="tabular-nums">
                  {c.useCount}
                  {c.maxUses != null ? ` / ${c.maxUses}` : ""}
                </td>
                <td>{c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : "—"}</td>
                <td>{c.active ? "Active" : "Revoked"}</td>
                <td className="text-right">
                  <button
                    type="button"
                    className="min-h-11 px-2 text-xs underline"
                    onClick={async () => {
                      await setActive({ data: { id: c.id, active: !c.active } });
                      await refresh();
                    }}
                  >
                    {c.active ? "Revoke" : "Re-activate"}
                  </button>
                </td>
              </tr>
            ))}
            {codes.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-3 text-muted-foreground">
                  No press codes issued yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>

      <PressBrandAssets />
    </main>
  );
}
